import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SchemaBuilder } from '../../structured-data.model';
import {
  BreadcrumbsService,
  CmsFacade,
  CultureFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import { BoelsBaseSite, BreadcrumbsModel, Page } from '@boels/models';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class BoelsBreadcrumbSchemaBuilder implements SchemaBuilder {
  private breadcrumbsService = inject(BreadcrumbsService);
  private cmsFacade = inject(CmsFacade);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);
  private translocoService = inject(TranslocoService);

  build(): Observable<any> {
    return combineLatest([
      this.cmsFacade.activeLoaded$,
      this.breadcrumbsService.getBreadCrumbData(),
      this.siteContextFacade.getActiveBaseSiteEntity(),
      this.cultureFacade.getActiveCulture(),
      this.translocoService.selectTranslate(
        'breadcrumb.home',
        {},
        { scope: 'header' },
      ),
    ]).pipe(
      map(([activePage, breadcrumbData, boelsBaseSite, culture, translation]) =>
        this.collect(
          activePage,
          breadcrumbData?.breadcrumbs,
          boelsBaseSite,
          culture,
          translation,
        ),
      ),
    );
  }

  protected collect(
    page: Page | undefined,
    breadcrumbs: BreadcrumbsModel[] | undefined,
    baseSite: BoelsBaseSite,
    culture: string,
    homeTranslation: string,
  ): {
    '@context'?: string;
    '@type'?: string;
    itemListElement?: {
      '@type': string;
      position: number;
      item: {
        '@id': string;
        name?: string;
      };
    }[];
  } {
    const crumbs =
      breadcrumbs?.map((crumb: BreadcrumbsModel, index: number) => {
        const code = `${baseSite.baseSiteUrl}/${culture}${crumb.url}`;
        const name = crumb.name === 'Home' ? homeTranslation : crumb.name;

        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': code,
            name: name,
          },
        };
      }) || [];

    if (page?.title && page.uid !== 'productList') {
      crumbs.push({
        '@type': 'ListItem',
        position: crumbs.length + 1,
        item: {
          '@id': page.title,
          name: page.title,
        },
      });
    }

    if (!crumbs.length) return {};

    return {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs,
    };
  }
}
